// src/pages/projects/ProjectContainer.jsx
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Box, CircularProgress, Alert, AlertTitle, Button, Snackbar
} from '@mui/material';
import { Link } from 'react-router-dom';

// Import components
import ProjectHeader from './container/ProjectHeader';
import ProjectDescription from './container/ProjectDescription';
import ProjectMenu from './container/ProjectMenu';

// Import hooks and stores
import useConfirmDialog from '../../hooks/useConfirmationDialog';
import useAuthStore from '../../stores/authStore';
import useProjectStore from '../../stores/projectStore';
import useProjectTemplateStore from '../../stores/projectTemplateStore';
import useUIStore from '../../stores/uiStore';
import DataStateHandler from '../core/data/DataStateHandler';

/**
 * ProjectContainer serves as the main container for all project views
 * Updated with cleaner Asana-like UI
 */
const ProjectContainer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const titleInputRef = useRef(null);

  // State
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState('');
  const [isStarred, setIsStarred] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState('error');

  // Project store
  const { 
    currentProject, 
    fetchProject, 
    deleteProject,
    updateProject,
    updateProjectStatus,
    isLoading, 
    criticalError,
    operationError,
    clearError,
    clearOperationError
  } = useProjectStore();

  // UI store for notifications
  const { addNotification } = useUIStore();

  // Project template store
  const { createTemplateFromProject } = useProjectTemplateStore();

  // Confirm dialog hook
  const [ConfirmDialogComponent, confirmDelete] = useConfirmDialog();

  // Load project data
  useEffect(() => {
    fetchProject(id);
  }, [id, fetchProject]);

  // Update title value when project changes
  useEffect(() => {
    if (currentProject) {
      setTitleValue(currentProject.name);
      setIsStarred(currentProject.isStarred || false);
    }
  }, [currentProject]);

  // Focus input when editing title
  useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
  }, [isEditingTitle]);

  // Show toast for operation errors
  useEffect(() => {
    if (operationError) {
      setToastMessage(operationError);
      setToastSeverity('error');
      setToastOpen(true);
      clearOperationError();
    }
  }, [operationError, clearOperationError]);

  // Menu handlers
  const handleOpenMenu = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
  };

  // Handle editing project title
  const handleStartEditingTitle = () => {
    setIsEditingTitle(true);
  };

  const handleTitleChange = (e) => {
    setTitleValue(e.target.value);
  };

  const handleTitleSubmit = async (e) => {
    if (e.key === 'Enter' || e.type === 'blur') {
      if (titleValue.trim() && titleValue !== currentProject.name) {
        try {
          await updateProject(id, { name: titleValue });
        } catch (err) {
          console.log('Failed to update project name:', err);
          setTitleValue(currentProject.name); // Reset on error
        }
      } else {
        setTitleValue(currentProject.name); // Reset if empty or unchanged
      }
      setIsEditingTitle(false);
    } else if (e.key === 'Escape') {
      setTitleValue(currentProject.name);
      setIsEditingTitle(false);
    }
  };

  // Handle delete project
  const handleDeleteProject = async () => {
    handleCloseMenu();
    
    const confirmed = await confirmDelete({
      title: 'Delete Project',
      content: `Are you sure you want to delete "${currentProject.name}"? This action cannot be undone and will delete all associated tasks and data.`,
      confirmText: 'Delete Project',
      cancelText: 'Cancel',
      isDelete: true
    });
    
    if (confirmed) {
      try {
        await deleteProject(id);
        navigate('/projects');
      } catch (err) {
        console.log('Failed to delete project:', err);
      }
    }
  };

  // Handle save as template
  const handleSaveAsTemplate = async () => {
    handleCloseMenu();
    
    const templateName = prompt(`Save "${currentProject.name}" as a template. Enter template name:`);
    if (!templateName || !templateName.trim()) return;

    try {
      await createTemplateFromProject(currentProject._id, {
        name: templateName.trim(),
        description: `Template based on ${currentProject.name}`,
        category: 'custom'
      });
      
      setToastMessage('Project saved as template successfully!');
      setToastSeverity('success');
      setToastOpen(true);
    } catch (error) {
      setToastMessage('Failed to save project as template');
      setToastSeverity('error');
      setToastOpen(true);
    }
  };

  // Handle status change
  const handleStatusChange = async (status) => {
    handleCloseMenu();
    
    try {
      await updateProjectStatus(id, status);
    } catch (err) {
      console.log('Failed to update project status:', err);
    }
  };

  // Toggle starred status
  const handleToggleStar = async () => {
    try {
      await updateProject(id, { isStarred: !isStarred });
      setIsStarred(!isStarred);
    } catch (err) {
      console.log('Failed to update starred status:', err);
    }
  };

  // Check if user has edit permissions for this project
  const canEdit = useCallback(() => {
    if (!currentProject || !user) return false;
    
    // Admins can always edit
    if (
      user.role === 'superadmin' || 
      user.role === 'admin' || 
      user.role === 'agency-admin'
    ) {
      return true;
    }
    
    // Check if user is in project team with edit permissions
    return currentProject.team?.some(member => 
      member.user?._id === user.id && 
      member.permissions?.canEdit
    );
  }, [currentProject, user]);

  // If we're loading and have no project yet, show full loading state
  if (isLoading && !currentProject) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh'
      }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100%',
     
    }}>
      {/* Project Header - Asana Style - Show even with errors */}
      {currentProject && (
        <>
          <ProjectHeader 
            project={currentProject}
            isEditingTitle={isEditingTitle}
            titleValue={titleValue}
            titleInputRef={titleInputRef}
            isStarred={isStarred}
            canEdit={canEdit()}
            onOpenMenu={handleOpenMenu}
            onStartEditingTitle={handleStartEditingTitle}
            onTitleChange={handleTitleChange}
            onTitleSubmit={handleTitleSubmit}
            onToggleStar={handleToggleStar}
          />
          
          {/* Project Description (optional) - collapsible */}
          <ProjectDescription description={currentProject.description} />
        </>
      )}
      
      {/* Critical Error Message - Above content, not replacing it */}
      {criticalError && (
        <Box sx={{ mb: 2, mx: 2 }}>
          <Alert 
            severity="error" 
            onClose={() => clearError()}
            action={
              <Button color="inherit" size="small" onClick={() => fetchProject(id)}>
                Retry
              </Button>
            }
          >
            <AlertTitle>Error</AlertTitle>
            {criticalError}
          </Alert>
        </Box>
      )}
      
      {/* View Content - Always rendered if project exists, or DataStateHandler if not */}
      <Box sx={{ flexGrow: 1, overflow: 'auto', minHeight: 0, bgcolor:'background.paper' }}>
        {currentProject ? (
          <Outlet context={{ project: currentProject, canEdit: canEdit() }} />
        ) : (
          <DataStateHandler 
            isLoading={isLoading} 
            error={criticalError && !currentProject}
            onRetry={() => fetchProject(id)}
            emptyMessage="Project not found"
            errorMessage={criticalError}
          />
        )}
      </Box>
      
      {/* Project Menu */}
      <ProjectMenu 
        anchorEl={menuAnchorEl}
        project={currentProject}
        onClose={handleCloseMenu}
        onDelete={handleDeleteProject}
        onStatusChange={handleStatusChange}
        onStartEditingTitle={handleStartEditingTitle}
        onSaveAsTemplate={handleSaveAsTemplate}
      />
      
      {/* Confirm Dialog */}
      <ConfirmDialogComponent />
      
      {/* Operation Error Toast */}
      <Snackbar
        open={toastOpen}
        autoHideDuration={6000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setToastOpen(false)} 
          severity={toastSeverity}
          sx={{ width: '100%' }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProjectContainer;